import React, { createContext, ReactElement, useCallback, useEffect, useState } from 'react';
import { Validation, ValidationState } from '@vp/upload-and-print-validator';
import { AnalyticsEventData, sendEventTracking } from 'src/components/Analytics';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { text } from 'src/utils/localizationUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { getValidationsForProduct, processValidations } from 'src/utils/validationUtil';

export interface ValidationContextProps {
  validations: Validation[] | null;
  validationPlaceholders: Validation[] | null;
  setValidations: (newValidations: Validation[] | null) => void;
  isValidating: boolean;
  setIsValidating: (isValidating: boolean) => void;
  setNeedsRevalidation: (needsRevalidation: boolean) => void;
  needsRevalidation: boolean;
}

export const ValidationContext = createContext<ValidationContextProps>({
  validations: null,
  validationPlaceholders: null,
  setValidations: () => {
    return;
  },
  isValidating: false,
  setIsValidating: () => {
    return;
  },
  setNeedsRevalidation: () => {
    return;
  },
  needsRevalidation: true,
});

export interface ValidationContextProviderProps {
  children?: React.ReactNode;
}

export const ValidationContextProvider = ({ children }: ValidationContextProviderProps): ReactElement => {
  const productContext = useProductContext();
  const { trackingConfiguration } = useTrackingContext();
  const [validationPlaceholders, setValidationPlaceholders] = useState<Validation[] | null>(null);
  const [validations, setValidationsInternal] = useState<Validation[] | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [needsRevalidation, setNeedsRevalidation] = useState(true);

  const trackingDataForProduct = createTrackingDataForProduct(productContext.productInformation);

  const setValidations = useCallback(
    (newValidations: Validation[] | null) => {
      if (newValidations) {
        console.log('Validations', newValidations);
      }

      const processedValidations = newValidations
        ? processValidations(newValidations, productContext.productOptionTranslations)
        : newValidations;
      setValidationsInternal(processedValidations);

      const unvalidatedValidationsExist =
        processedValidations?.some(validation => validation.state === ValidationState.UnValidated) ?? false;

      processedValidations?.forEach(validation => {
        if (validation.state === 'Error' || validation.state === 'Warning') {
          sendEventTracking({
            ...trackingConfiguration,
            //Not spreading trackingDataForProduct here because that would cause this useCallback to have a dependency on trackingDataForProduct,
            //which would be a new object every update, causing this useCallback to be recalculated every update, even if the actual
            //properties did not change
            productId: trackingDataForProduct.product_id,
            name: trackingDataForProduct.name,
            core_product_id: trackingDataForProduct.core_product_id,
            core_product_version: trackingDataForProduct.core_product_version,
            action: 'Error Message Displayed',
            category: 'Design - Upload and Print',
            label: text(validation.message, validation.tokenValues),
          } as AnalyticsEventData);
        }
      });

      setNeedsRevalidation(unvalidatedValidationsExist);
    },
    [
      trackingDataForProduct.product_id,
      trackingDataForProduct.name,
      trackingDataForProduct.core_product_id,
      trackingDataForProduct.core_product_version,
      productContext.productOptionTranslations,
      trackingConfiguration,
    ]
  );

  useEffect(() => {
    let isStale = false;

    async function getValidations() {
      if (productContext.isProductReady) {
        const requiredValidations = await getValidationsForProduct(productContext.productInformation);
        if (isStale) {
          return;
        }

        const processedValidations = processValidations(requiredValidations, productContext.productOptionTranslations);
        setValidationPlaceholders(processedValidations);
      }
    }

    getValidations();

    return () => {
      isStale = true;
    };
  }, [productContext]);

  return (
    <ValidationContext.Provider
      value={{
        validations: validations,
        validationPlaceholders: validationPlaceholders,
        setValidations,
        isValidating,
        setIsValidating,
        setNeedsRevalidation,
        needsRevalidation,
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};
